/**
 * APPOINTMENT-BOOK-V1 -- the PURE derivation layer behind the Appointments
 * planning calendar (AppointmentCalendarPage.tsx).
 *
 * Repo testability idiom (ClinicDayBoard.tsx): pure helpers here, a view that
 * renders from props, a container that wires state. Everything in this module
 * is a total function of its arguments -- no React, no fetching, no Date.now()
 * except through an INJECTED instant.
 *
 * WHAT THIS MODULE OWNS
 *  - the COLUMN model: a LEADING "Unassigned" column plus the clinic's ACTIVE
 *    roster in served order (the server already name-orders it). An
 *    unassigned appointment is a real, honest state -- it is never hidden and
 *    never bucketed into a doctor.
 *  - the TIME BAND model: bands derived from the clinic's CONFIGURED window
 *    (booking.schedule_rules, resolved SERVER-side and shipped as
 *    scheduleWindow). This surface keeps NO client-side copy of clinic hours
 *    (ONE-RULE-SOURCE, DaySlotPicker.tsx). Rows that fall OUTSIDE the
 *    configured window still get a band -- a legacy or out-of-hours row is
 *    shown, never dropped.
 *  - the DATE STEPPER arithmetic (prev / next / today) on 'YYYY-MM-DD',
 *    correct across month and year rollover.
 *  - the MOBILE single-column projection (<=720px): the SAME blocks in one
 *    time-ordered list with the doctor as a labeled field.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT OWN
 *  - status LABELS or TONES: the shared StatusChip canon
 *    (frontend/src/dashboard/ui/StatusChip.tsx) is the ONE owner; this
 *    surface must not become a seventh appointment-label home (R-C).
 *  - the patient label: dayBoardPatientLabel (ClinicDayBoard.tsx) is reused
 *    verbatim -- one golden-record-first label, not a second copy.
 *  - anything beyond the block's CLOSED content list (time, patient label,
 *    status chip). No channel, no money, no ops-status, no intake chip, no
 *    arrival time, no plan counts, no doctor-profile field.
 */

import type {
  ClinicRosterDoctor,
  ClinicScheduleWindow,
  TodayAppointment,
} from '@/dashboard/types';
import { formatIstTime, istMinutesOfDay } from '@/lib/datetime-ist';

/** The leading column's stable key. Not a doctor id -- a real state. */
export const CALENDAR_UNASSIGNED_COLUMN_KEY = 'unassigned';

/** The leading column's header text (the honest no-assignment truth; the
 *  SAME word the day board's doctor cell uses). */
export const CALENDAR_UNASSIGNED_COLUMN_LABEL = 'Unassigned';

/** Rendered when a roster doctor's specialty is blank on the wire -- honest,
 *  never a blank cell and never an invented specialty. */
export const CALENDAR_SPECIALTY_UNSET_LABEL = 'Specialty not set';

/** The mobile projection's doctor field label (doctor becomes a FIELD, not a
 *  column, at <=720px). */
export const CALENDAR_MOBILE_DOCTOR_FIELD_LABEL = 'Doctor';

/** Fallback band length when the server ships no window (minutes). Matches
 *  the booking-rules code default so the two never disagree visually. */
const FALLBACK_BAND_MINUTES = 60;

/** One calendar column: the leading Unassigned lane, then one per doctor. */
export interface CalendarColumn {
  readonly key: string;
  /** null == the Unassigned lane. */
  readonly doctorId: number | null;
  /** Header line 1: the roster name (or the Unassigned label). */
  readonly name: string;
  /** Header line 2: the roster specialty. null == not set on the wire.
   *  The Unassigned lane carries none (it is not a person). */
  readonly specialty: string | null;
}

/** One time band (a row of the grid). Half-open [start, end) IST minutes. */
export interface CalendarTimeBand {
  readonly key: string;
  readonly startMinutes: number;
  readonly endMinutes: number;
  /** 12-hour IST label, e.g. '9:00 AM'. */
  readonly label: string;
}

/** One rendered appointment block -- the CLOSED content list. */
export interface CalendarBlock {
  readonly appointmentId: number;
  /** The block's 12-hour IST time, e.g. '9:30 AM'. */
  readonly timeLabel: string;
  /** The golden-record-first patient label (dayBoardPatientLabel output). */
  readonly patientLabel: string;
  /** The stored appointment status token -- rendered through StatusChip. */
  readonly status: string;
  readonly columnKey: string;
  readonly bandKey: string;
}

/**
 * The columns for one clinic day: the LEADING Unassigned lane, then the
 * clinic's ACTIVE roster in SERVED order (the read already orders by name).
 * The roster is served unconditionally -- a doctor with zero appointments
 * still gets a column, so an empty day still shows the clinic's shape.
 */
export function buildCalendarColumns(
  clinicDoctors: readonly ClinicRosterDoctor[],
): CalendarColumn[] {
  const columns: CalendarColumn[] = [
    {
      key: CALENDAR_UNASSIGNED_COLUMN_KEY,
      doctorId: null,
      name: CALENDAR_UNASSIGNED_COLUMN_LABEL,
      specialty: null,
    },
  ];
  for (const doctor of clinicDoctors) {
    columns.push({
      key: `doctor-${doctor.id}`,
      doctorId: doctor.id,
      name: doctor.name,
      specialty:
        typeof doctor.specialty === 'string' && doctor.specialty.trim() !== ''
          ? doctor.specialty.trim()
          : null,
    });
  }
  return columns;
}

/** The column key a row belongs in. A null/unknown doctor id is the honest
 *  Unassigned lane; a doctor id with no roster column (an INACTIVE doctor
 *  still referenced by an old row) also lands there rather than vanishing. */
export function calendarColumnKeyForRow(
  row: Pick<TodayAppointment, 'assignedDoctorId'>,
  columns: readonly CalendarColumn[],
): string {
  const doctorId = row.assignedDoctorId ?? null;
  if (doctorId === null) return CALENDAR_UNASSIGNED_COLUMN_KEY;
  const match = columns.find((c) => c.doctorId === doctorId);
  return match?.key ?? CALENDAR_UNASSIGNED_COLUMN_KEY;
}

/** Floor a minute-of-day onto the band grid anchored at `originMinutes`. */
function floorToBand(
  minutes: number,
  originMinutes: number,
  bandMinutes: number,
): number {
  const delta = minutes - originMinutes;
  const steps = Math.floor(delta / bandMinutes);
  return originMinutes + steps * bandMinutes;
}

/**
 * The day's time bands. The clinic's CONFIGURED window (server-resolved from
 * booking.schedule_rules) sets the grid origin, length, and default extent;
 * the served rows then EXTEND it where a row sits outside those hours, so an
 * out-of-hours or legacy row is always visible. With no window on the wire
 * the bands derive from the rows alone (a degraded but honest grid), and an
 * empty day with no window yields no bands -- the caller renders its empty
 * state instead of a fake grid.
 */
export function buildCalendarTimeBands(args: {
  readonly scheduleWindow?: ClinicScheduleWindow | null;
  readonly rows: readonly TodayAppointment[];
}): CalendarTimeBand[] {
  const { scheduleWindow, rows } = args;
  const bandMinutes =
    scheduleWindow && Number.isFinite(scheduleWindow.slotMinutes) &&
    scheduleWindow.slotMinutes >= 5
      ? Math.floor(scheduleWindow.slotMinutes)
      : FALLBACK_BAND_MINUTES;

  const rowMinutes = rows
    .map((r) => istMinutesOfDay(r.appointmentTime))
    .filter((m): m is number => m !== null);

  const hasWindow =
    scheduleWindow !== null &&
    scheduleWindow !== undefined &&
    Number.isFinite(scheduleWindow.startMinutes) &&
    Number.isFinite(scheduleWindow.endMinutes) &&
    scheduleWindow.endMinutes > scheduleWindow.startMinutes;

  if (!hasWindow && rowMinutes.length === 0) return [];

  const origin = hasWindow
    ? Math.floor(scheduleWindow.startMinutes)
    : floorToBand(Math.min(...rowMinutes), 0, bandMinutes);

  let firstBandStart = origin;
  let lastBandStart = hasWindow
    ? floorToBand(
        Math.max(origin, Math.floor(scheduleWindow.endMinutes) - 1),
        origin,
        bandMinutes,
      )
    : origin;

  for (const minutes of rowMinutes) {
    const bandStart = floorToBand(minutes, origin, bandMinutes);
    if (bandStart < firstBandStart) firstBandStart = bandStart;
    if (bandStart > lastBandStart) lastBandStart = bandStart;
  }

  const bands: CalendarTimeBand[] = [];
  for (let start = firstBandStart; start <= lastBandStart; start += bandMinutes) {
    bands.push({
      key: String(start),
      startMinutes: start,
      endMinutes: start + bandMinutes,
      label: minutesToIstLabel(start),
    });
  }
  return bands;
}

/** IST minutes-of-day -> a 12-hour label ('9:00 AM'). Handles the
 *  out-of-window negative / past-midnight edges honestly. */
export function minutesToIstLabel(minutes: number): string {
  const normalized = ((Math.round(minutes) % 1440) + 1440) % 1440;
  let h = Math.floor(normalized / 60);
  const m = String(normalized % 60).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

/** The band key a row falls in, or null when the row has no readable time
 *  (never guessed into a band). */
export function calendarBandKeyForRow(
  row: Pick<TodayAppointment, 'appointmentTime'>,
  bands: readonly CalendarTimeBand[],
): string | null {
  const minutes = istMinutesOfDay(row.appointmentTime);
  if (minutes === null || bands.length === 0) return null;
  for (const band of bands) {
    if (minutes >= band.startMinutes && minutes < band.endMinutes) {
      return band.key;
    }
  }
  return null;
}

/**
 * Map every served row into its (band, column) cell. Rows with an unreadable
 * time are returned separately so the caller can render them honestly rather
 * than silently dropping them.
 */
export interface CalendarGrid {
  readonly columns: readonly CalendarColumn[];
  readonly bands: readonly CalendarTimeBand[];
  /** `${bandKey}|${columnKey}` -> the blocks in that cell (time-ordered). */
  readonly cells: ReadonlyMap<string, readonly CalendarBlock[]>;
  /** Rows whose appointment_time could not be read into a band. */
  readonly unplacedBlocks: readonly CalendarBlock[];
}

export function calendarCellKey(bandKey: string, columnKey: string): string {
  return `${bandKey}|${columnKey}`;
}

export function buildCalendarGrid(args: {
  readonly rows: readonly TodayAppointment[];
  readonly clinicDoctors: readonly ClinicRosterDoctor[];
  readonly scheduleWindow?: ClinicScheduleWindow | null;
  /** The ONE patient-label owner, injected (dayBoardPatientLabel). */
  readonly patientLabel: (row: TodayAppointment) => string;
}): CalendarGrid {
  const { rows, clinicDoctors, scheduleWindow, patientLabel } = args;
  const columns = buildCalendarColumns(clinicDoctors);
  const bands = buildCalendarTimeBands({
    ...(scheduleWindow !== undefined ? { scheduleWindow } : {}),
    rows,
  });

  const cells = new Map<string, CalendarBlock[]>();
  const unplacedBlocks: CalendarBlock[] = [];

  for (const row of rows) {
    const columnKey = calendarColumnKeyForRow(row, columns);
    const bandKey = calendarBandKeyForRow(row, bands);
    const block: CalendarBlock = {
      appointmentId: row.id,
      timeLabel: formatIstTime(row.appointmentTime),
      patientLabel: patientLabel(row),
      status: row.status,
      columnKey,
      bandKey: bandKey ?? '',
    };
    if (bandKey === null) {
      unplacedBlocks.push(block);
      continue;
    }
    const key = calendarCellKey(bandKey, columnKey);
    const bucket = cells.get(key);
    if (bucket) bucket.push(block);
    else cells.set(key, [block]);
  }

  return { columns, bands, cells, unplacedBlocks };
}

// --- FD-43 (RECEPTION-FIX-AND-POLISH-BATCH-V1): enterprise calendar rhythm --

/**
 * The DISPLAY projection of the band list: consecutive EMPTY bands (no
 * block in any column) collapse into ONE thin GAP row naming the span --
 * a 9am-8pm window with three appointments stops rendering forty empty
 * rows. A pure derivation over the SAME grid (never a second placement
 * computation); a single empty band stays a normal band (a one-row gap
 * reads better as an open slot than as a collapse).
 */
export type CalendarDisplayBand =
  | { readonly kind: 'band'; readonly band: CalendarTimeBand }
  | {
      readonly kind: 'gap';
      readonly key: string;
      /** First and last collapsed band labels ("10:00 AM" / "1:00 PM"). */
      readonly fromLabel: string;
      readonly toLabel: string;
      readonly bandCount: number;
    };

export function calendarDisplayBands(grid: CalendarGrid): CalendarDisplayBand[] {
  const bandIsEmpty = (band: CalendarTimeBand): boolean =>
    grid.columns.every(
      (c) => (grid.cells.get(calendarCellKey(band.key, c.key))?.length ?? 0) === 0,
    );
  const out: CalendarDisplayBand[] = [];
  let run: CalendarTimeBand[] = [];
  const flush = (): void => {
    if (run.length === 0) return;
    if (run.length === 1) {
      out.push({ kind: 'band', band: run[0] as CalendarTimeBand });
    } else {
      const first = run[0] as CalendarTimeBand;
      const last = run[run.length - 1] as CalendarTimeBand;
      out.push({
        kind: 'gap',
        key: `gap-${first.key}`,
        fromLabel: first.label,
        toLabel: last.label,
        bandCount: run.length,
      });
    }
    run = [];
  };
  for (const band of grid.bands) {
    if (bandIsEmpty(band)) {
      run.push(band);
    } else {
      flush();
      out.push({ kind: 'band', band });
    }
  }
  flush();
  return out;
}

/** One entry of the <=720px single-column projection: the SAME block plus
 *  the doctor rendered as a labeled FIELD instead of a column position. */
export interface CalendarStackEntry {
  readonly block: CalendarBlock;
  /** The column's name -- a doctor name or the Unassigned label. */
  readonly doctorLabel: string;
}

/**
 * The mobile projection: every served block in ONE time-ordered list (the
 * server already returns rows earliest-first; ties keep served order, so the
 * list can never disagree with the grid). Unplaced blocks stay in the list --
 * mobile never hides a row the desktop grid would show.
 */
export function calendarStackEntries(grid: CalendarGrid): CalendarStackEntry[] {
  const nameByColumn = new Map(grid.columns.map((c) => [c.key, c.name]));
  const ordered: CalendarBlock[] = [];
  for (const band of grid.bands) {
    for (const column of grid.columns) {
      const bucket = grid.cells.get(calendarCellKey(band.key, column.key));
      if (bucket) ordered.push(...bucket);
    }
  }
  ordered.push(...grid.unplacedBlocks);
  return ordered.map((block) => ({
    block,
    doctorLabel:
      nameByColumn.get(block.columnKey) ?? CALENDAR_UNASSIGNED_COLUMN_LABEL,
  }));
}

// --- Date navigation (pure 'YYYY-MM-DD' arithmetic) ------------------------

/**
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 (A4 BOUNDARY LAW -- NO SECOND DAY
 * DEFINITION): the four clinic-day functions this module used to define
 * MOVED, unchanged, into the shared day-scope primitive
 * (frontend/src/dashboard/ui/day-scope.ts) so every day-driven surface reads
 * ONE implementation. They are re-exported here under their original names,
 * so every existing import, consumer and pin resolves exactly as before and
 * no call site changed. New code should import from the primitive.
 *
 *   isCalendarDate    -> isClinicDate
 *   stepCalendarDate  -> stepClinicDate
 *   istCalendarToday  -> clinicToday
 *   calendarDateLabel -> clinicDateLabel
 */
export {
  isClinicDate as isCalendarDate,
  stepClinicDate as stepCalendarDate,
  clinicToday as istCalendarToday,
  clinicDateLabel as calendarDateLabel,
} from '@/dashboard/ui/day-scope';
